import axios from 'axios'
import { TL_BANK } from 'domains/payment-bank-transfer/constants'
import ApiUrlUtils from 'utils/api'
import ParamsUtil from 'utils/params-util'

const API_PATH = 'api/v2/admins/customers/orders'

const CustomerOrdersApiV2 = {

  /**
   * Get list of Customer Orders
   * Invoke `GET api/v2/admins/customers/orders
   * @param {Number} page - current page
   * @param {Number} per_page - data per page
   * @param {Number} sort_by - sort by value default: created_at
   * @param {Number} sort_dir - sort direction, (asc/desc)
   * @param {Number} number - order number
   * @param {String} status - current order status
   * @param {Boolean} is_cod - is COD order? true/false
   * @param {String} payment_method_name - order payment method
   * @param {String} discount_code - order discount/voucher code
   * @param {String} item_sku - items sku in certain order
   * @param {String} customer_name - Customer name
   * @param {String} email - Customer email
   * @param {String} customer_phone - Customer Phone
  */
  get: async ({
    //set default parameters
    page                = 1,
    per_page            = 10,
    sort_by             = 'created_at',
    sort_dir            = 'desc',
    number              = '',
    status              = '',
    is_cod              = '',
    payment_method_name = '',
    discount_code       = '',
    item_sku            = '',
    customer_name       = '',
    email               = '',
    customer_phone      = '',
    customer_id         = '',
    affiliate_name = ''
  }) => {
    let params = {
      page                : page,
      per_page            : per_page,
      sort_by             : sort_by,
      sort_dir            : sort_dir,
      number              : number,
      status              : status,
      is_cod              : is_cod,
      payment_method_name : payment_method_name,
      discount_code       : discount_code,
      item_sku            : item_sku,
      customer_name       : customer_name,
      email               : email,
      customer_phone      : customer_phone,
      customer_id         : customer_id,
      affiliate_name      : affiliate_name
    }
    const path = API_PATH

    params = ParamsUtil.removeEmptyAttributes(params)

    const response = axios.get(path, { params })
    return response
  },

  /**
   * Get detail of Customer Order by ID
   * Invoke `GET api/v2/admins/customers/orders/:id
   * @param {object} tableState - ReactTable final state
   * @param {object} additionalFilters - A plain object to get customize filters outside `tableState`
   */
  show: async (orderId) => {
    const path = `${API_PATH}/${orderId}`

    const response = await axios.get(path, {})
    return response
  },

  /**
   * Cancel Customer Order with status = pending.
   * Invoke `PATCH api/v2/admins/customers/orders/:id/cancel`
   * @param {number} id - Customer Order ID
   */
  cancel: async (id) => {
    const path = `${API_PATH}/${id}/cancel`

    const response = axios.patch(path)
    return response
  },

  patchItemsRefund: async ({ order_id, items }) => {

    const path = `${API_PATH}/items/refund`

    const response = axios.patch(path, { order_id, items })
    return response
  },

  /**
   * Patch COD Order Item status
   * Invoke `GET api/v2/admins/customers/orders/items/cod_update_status
   * @param {object} order_id - id of order
   * @param {object<Array>} confirmed_item_ids - item id list that will confirmed to COD
   * @param {object<Array>} cancelled_item_ids - item id list that will cancelled
   */
  patchCodItemsStatus: async ({ order_id, confirmed_item_ids, cancelled_item_ids }) => {

    const path = `${API_PATH}/items/cod_update_status`

    const response = axios.patch(path, { order_id, confirmed_item_ids, cancelled_item_ids })
    return response
  },

  downloadTodayOrdersCsv: async ({ params }) => {
    const path = `${API_PATH}/today_orders_csv`

    const response = await axios.get(path, { params })
    return response
  },


  /**
   * Get list of Payment Bank Transfer
   * Invoke `GET api/v2/admins/customers/orders/payments/bank_transfer
   * @param {object} tableState - ReactTable final state
   * @param {object} additionalFilters - A plain object to get customize filters outside `tableState`
   */
  getBankTransfers: async (params) => {
    const path = `${API_PATH}/payments/bank_transfer`

    const response = axios.get(path, { params })
    return response
  },

  /**
   * Update status payment bank transfer to be completed.
   * Invoke `PATCH api/v2/admins/customers/orders/payments/bank_transfer/:id/confirm`
   * @param {number} id - Payment bank transfer ID
   * @param {string} remarks - Notes
   */
  confirmPaymentBankTransfer: async (
    id,
    remarks
  ) => {
    const path = `${API_PATH}/payments/bank_transfer/${id}/confirm`
    const body = {
      remarks: remarks
    }

    const response = axios.patch(path, body)
    return response
  },

  /**
   * Update status payment bank transfer to be rejected.
   * Invoke `PATCH api/v2/admins/customers/orders/payments/bank_transfer/:id/reject`
   * @param {number} id - Payment bank transfer ID
   * @param {string} remarks - Notes
   */
  rejectPaymentBankTransfer: async (
    id,
    remarks
  ) => {
    const path = `${API_PATH}/payments/bank_transfer/${id}/reject`
    const body = {
      remarks: remarks
    }

    const response = axios.patch(path, body)
    return response
  },

  /**
   * Create Payment Bank Transfer
   * Invoke `POST api/v2/admins/customers/orders/payments/bank_transfer
   * @param {object} values - values from formik
   */
  postBankTransfers: async (values) => {
    const path = `${API_PATH}/payments/bank_transfer`

    //clone object without reference to keep the field value in formik  adjust values to payload
    const params = JSON.parse(JSON.stringify(values))


    //set credit_bank_name to select input so it's more uniform instead of type the credit_bank_name,
    //since we can't cover all the bank options, we provide field for user to type the bank name if they choose others
    //the value will set to credit_bank_name_others key. this key only temporary because backend doesn't need this key
    //we get the value and assign to credit_bank_name, after that remove the object from params that will send to backend
    if (values.credit_bank_name_others) params.credit_bank_name = params.credit_bank_name_others
    delete params.credit_bank_name_others

    //get debit_tl_bank_id based on debit_tl_bank name
    const getKey = (value) => Object.keys(TL_BANK).find((key) => TL_BANK[key] === value)

    params.debit_tl_bank_id = getKey(params.debit_tl_bank)

    const response = axios.post(path, params)
    return response
  },
  /**
   *
   * @param {number} customerId
   * @param {number} addressId
   */
  postShipmentOptions: async (customerId, addressId, cartItemIds) => {
    const url      = `${API_PATH}/shipment_options`
    const body     = {
      customer_id     : customerId,
      user_address_id : addressId,
      cart_item_ids   : cartItemIds
    }
    const response = axios.post(url, body)
    return response
  },

  /**
   *
   * @param {number} shippingAddressId
   */
  getPaymentOptions: async (shippingAddressId) => {
    const url      = `${API_PATH}/payment_options?shipping_address_id=${shippingAddressId}`
    const response = axios.get(url)
    return response
  },

  /**
   *
   * @param {string} code - voucher code keyword to search
   * @param {boolean} show_on_checkout - is vocuher code shown on checkout
   */
  getVoucher: async ({
    code = '',
    show_on_checkout = '',
    page = 1,
    per_page = 25
  }) => {
    const url      = `${API_PATH}/vouchers`
    const params   = { code, show_on_checkout, page, per_page }
    const response = await axios.get(url, { params })

    return response
  },

  /**
   *
   * @param {number} customer_id - id of customer number
   * @param {string} code - voucher code keyword to search
   * @param {number} shipping_address_id - shipping address id of customer
   * @param {number} payment_method_id - payment method id of choosen customer
   * @param {number} shipments - shipments detail that choosen by customer with items included
   */
  applyVoucher: ({
    customer_id,
    code,
    shipping_address_id,
    payment_method_id,
    shipment,
    cart_item_ids
  })  => {
    const url    = `${API_PATH}/vouchers/apply`
    const params = { customer_id, code, shipping_address_id, payment_method_id, shipment, cart_item_ids }

    return axios.post(url, params)
  },

  /**
   * @param {number} customerId - customer id number
   * @param {object} address - address object
   * @param {string} voucherCode - string of the voucher code applied
   * @param {object} paymentMethod - paymentMethod object
   * @param {Array<Object>} shipments - Array of shipment objects.
   */
  postOrder: async (
    customerId,
    address,
    voucherCode,
    paymentMethod,
    shipment,
    cartItemIds,
    affiliateName
  ) => {

    const body = {
      order: {
        customer_id         : customerId,
        shipping_address_id : address.id,
        voucher_code        : voucherCode,
        payment_method_id   : paymentMethod.id,
        shipment            : shipment,
        cart_item_ids       : cartItemIds,
        affiliate_name      : affiliateName
      }
    }

    const url      = `${API_PATH}`
    const response = axios.post(url, body)
    return response
  },

  /**
   * Create in the backend:
   * - Customer::Order::Payment
   * - Customer::Order::Payment::BankTransfer
   * @param {Number} customerId - id of the customer thar belongs to order
   * @param {Number} paymentMethodId - id of the selected payment method from checkout page
   * @param {Number} orderId - id of the order customer is making payment for
   */
  postBankTransfer: async (paymentId, orderId) => {
    const body = {
      order_id   : orderId,
      payment_id : paymentId
    }

    const url      = `${API_PATH}/payments/bank_transfer`
    const response = axios.post(url, body)
    return response
  },

  /**
   * Creates a Customer::Order::Payment for an Order in the backend
   * Order Payments have a url in the format of api/v2/...?param=value
   * Since we are making a post, extract param=value to place into body
   * @param {Number} customerId - id of the customer thar belongs to order
   * @param {String} paymentMethodUrl - payment endpoint url
   * @param {Number} orderId - id of the order customer is making payment for
   */
  postVirtualAccount: async (paymentMethodUrl, paymentId, orderId) => {
    // default request body values
    var body = {
      order_id   : orderId,
      payment_id : paymentId
    }

    const paymentMethodParams = ApiUrlUtils.urlParams(paymentMethodUrl)
    body                      = {
      ...body,
      ...paymentMethodParams
    }

    const url      = `${API_PATH}/payments/nicepay`
    const response = axios.post(url, body)
    return response
  },

  /**
   * Create in the backend:
   * - Customer::Order::Payment
   * @param {Number} paymentMethodId - id of the selected payment method from checkout page
   */
  postOfflineCreditCard: async (paymentId) => {
    const body = {
      payment_id: paymentId
    }

    const url      = `${API_PATH}/payments/offline/credit_card`
    const response = axios.post(url, body)
    return response
  },

  /**
   * Create in the backend:
   * - Customer::Order::Payment
   * @param {Number} paymentMethodId - id of the selected payment method from checkout page
   */
  postOfflineQris: async (paymentId) => {
    const body = {
      payment_id: paymentId
    }

    const url      = `${API_PATH}/payments/offline/qris`
    const response = axios.post(url, body)
    return response
  },

  /**
   * Create in the backend:
   * - Customer::Order::Payment with credit_card/installment_credit_card and payment-link as redirect_url value
   * @param {Number} paymentMethodId - id of the selected payment method from checkout page
   */
  postMidtransCreditCard: async (paymentId) => {
    const body = {
      payment_id: paymentId
    }

    const url      = `${API_PATH}/payments/midtrans/credit_card`
    const response = axios.post(url, body)
    return response
  },

  /**
   * Create in the backend:
   * - Customer::Order::Payment with midtrans qris
   * @param {Number} paymentMethodId - id of the selected payment method from checkout page
   */
  postMidtransQris: async (paymentId) => {
    const body = {
      payment_id: paymentId
    }

    const url      = `${API_PATH}/payments/midtrans/qris`
    const response = axios.post(url, body)
    return response
  },

  /**
   * Assign sales admin to the customer_order
   * @param {Number} order_id - id of the selected customer_order
   * @param {Number} admin_id - id of the selected sales admin
   */
  assignSalesAdmin: async ({
    order_id,
    admin_id
  }) => {
    const body = {
      order_id : order_id,
      admin_id : admin_id
    }

    const url      = `${API_PATH}/assign_sales_admin`
    const response = axios.post(url, body)
    return response
  },

  /**
   * Download unlinked bank transaction orders csv for certain date range
   * Invoke `GET admins/finance/orders/csv`
   * @param {String} ordersDateFrom - start date, order.created_at
   * @param {String} ordersDateTo - end date, order.created_at
   */
  downloadBankTransactionOrder: async ({ ordersDateFrom, ordersDateTo, isBankStatementLinked }) => {
    const params   = {
      orders_date_from         : ordersDateFrom,
      orders_date_to           : ordersDateTo,
      is_bank_statement_linked : isBankStatementLinked
    }
    const path     = 'admins/finance/orders/csv'
    const response = await axios.get(path, { params })
    return response
  }
}

export default CustomerOrdersApiV2
